import React, { useEffect, useMemo, useRef, useState } from "react";
import { applyListLayout, normalizeImportItems } from "./importListLayout";
import { listsNotOnPool, prettyImportName, resolvePoolLists } from "./contactPoolLists";

const Check = ({ on }) => (
  <span className={`oj-listpick-check${on ? " on" : ""}`} aria-hidden="true">
    {on && (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )}
  </span>
);

/**
 * One-time add of import lists onto an agent that has already run.
 * Lists already on the agent stay put. This does not reopen status, tag,
 * or delivery.
 */
const AddAgentLists = ({
  pool,
  importOptions = [],
  layout,
  busy = false,
  onAdd,
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const wrapRef = useRef(null);
  const mode = resolvePoolLists(pool).mode;
  const available = useMemo(() => {
    const items = normalizeImportItems(importOptions);
    const { visible, hidden } = applyListLayout(items, layout);
    const names = new Set(listsNotOnPool(pool, [...visible, ...hidden].map((item) => item.name)));
    return [...visible, ...hidden].filter((item) => names.has(item.name));
  }, [importOptions, layout, pool]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (name) => {
    setPicked((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  const submit = async () => {
    const names = listsNotOnPool(pool, picked);
    if (!names.length || busy) return;
    const ok = await onAdd(names);
    if (ok === false) return;
    setPicked([]);
    setOpen(false);
  };

  const label = mode === "any" ? "Queue a list that arrived later" : "Add another list";
  const hint = mode === "any"
    ? "This agent already targets every list. This queues the people on the lists you pick, once, with the same status and tag."
    : "The lists already on this agent stay. People on the new list are queued once, with the same status and tag.";

  return (
    <div className="oj-add-lists">
      <div className="oj-sb-field">
        <label>{label}</label>
        <span className="oj-sb-field-hint">{hint}</span>
      </div>
      {available.length === 0 ? (
        <span className="oj-sb-field-hint">Every import is already on this agent.</span>
      ) : (
        <div className="oj-listpick" ref={wrapRef}>
          <div className="oj-listpick-anchor">
            <button
              type="button"
              className={`oj-listpick-trigger${open ? " open" : ""}${picked.length ? " active" : ""}`}
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={open}
              disabled={busy}
            >
              <span className="oj-listpick-trigger-label">
                {picked.length === 0
                  ? "Choose lists"
                  : picked.length === 1
                    ? prettyImportName(picked[0])
                    : `${picked.length} lists`}
              </span>
              <svg className={`oj-listpick-chevron${open ? " open" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {open && (
              <div className="oj-listpick-panel" role="listbox" aria-multiselectable="true">
                <div className="oj-listpick-list">
                  {available.map((item) => {
                    const on = picked.includes(item.name);
                    return (
                      <button
                        key={item.name}
                        type="button"
                        className={`oj-listpick-item${on ? " selected" : ""}`}
                        onClick={() => toggle(item.name)}
                      >
                        <Check on={on} />
                        <span className="oj-listpick-item-body">
                          <span className="oj-listpick-item-name">{item.name}</span>
                          {item.imported_count != null && (
                            <span className="oj-listpick-item-meta">{item.imported_count} contacts</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        className="oj-topbar-btn primary oj-add-lists-btn"
        onClick={submit}
        disabled={busy || picked.length === 0}
      >
        <i className={`fa ${busy ? "fa-spinner fa-spin" : "fa-plus"}`} aria-hidden="true" />
        {busy ? "Adding…" : "Add to this agent"}
      </button>
    </div>
  );
};

export default AddAgentLists;
